import * as THREE from "three";
import type { DishKind } from "../game/dishes";
import { DISH_PIT, type Box, type Station } from "../game/kitchen";
import { box, paint, span, steel, type Room } from "./build";
import type { RoomLook } from "./looks";
import { handWashSign, quarryTile, specialsBoard, subwayTile } from "./textures";

// The dish pit's fixed parts (the parked dishwashing restaurants), built once
// from `kitchen.ts`'s `DISH_PIT` layout: walls and floor, the dish line along
// the back wall (tub counter, sink, drainboard, machine), the wire shelving
// by the pass, and the details that make it a diner kitchen. What moves
// (dishes, racks, the machine's hood, the clock's hands) is handed back for
// `KitchenScene` to drive.

const { width: WIDTH, depth: DEPTH, height: HEIGHT } = DISH_PIT;
const STATIONS = DISH_PIT.stations as Record<"tub" | "sink" | "rack" | "machine" | "shelf", Box>;

/**
 * Where each kind of clean dish goes on the wire shelf: which level (bottom
 * up), how far along it, and where the `i`th one sits from there
 * ([dx, dy, dz, upside down]).
 */
const SHELF_SPOTS: Record<DishKind, { level: number; z: number; at: (i: number) => [number, number, number, boolean] }> = {
  plate: { level: 2, z: 0.4, at: (i) => [0, i * 0.026, 0, false] },
  bowl: { level: 1, z: 0.4, at: (i) => [0, Math.floor(i / 2) * 0.05, (i % 2) * 0.22, false] },
  mug: { level: 3, z: 0.3, at: (i) => [-0.06 + (i % 2) * 0.13, 0.1, Math.floor(i / 2) * 0.13, true] },
  cup: { level: 3, z: 1.2, at: (i) => [-0.06 + (i % 2) * 0.11, 0.062, Math.floor(i / 2) * 0.1, true] },
  sauce: { level: 2, z: 1.2, at: (i) => [0, Math.floor(i / 3) * 0.014, (i % 3) * 0.11, false] },
  wok: { level: 0, z: 0.5, at: (i) => [0, 0.004, i * 0.4, false] },
  basket: { level: 1, z: 1.4, at: (i) => [0, i * 0.02, 0, false] },
  tumbler: { level: 3, z: 1.4, at: (i) => [0, i * 0.02, 0, true] },
  shotglass: { level: 3, z: 1.6, at: (i) => [0, 0.07, i * 0.07, true] },
  pitcher: { level: 2, z: 1.6, at: (i) => [0, 0, i * 0.12, false] },
};

export function buildRoom(restaurantName: string, look: RoomLook): Room {
  const g = new THREE.Group();
  const highlight: Partial<Record<Station, THREE.MeshStandardMaterial[]>> & Record<"tub" | "sink" | "rack" | "machine" | "shelf", THREE.MeshStandardMaterial[]> = {
    tub: [],
    sink: [],
    rack: [],
    machine: [],
    shelf: [],
  };

  // --- floor, walls, ceiling --------------------------------------------------
  const floor = new THREE.Mesh(new THREE.PlaneGeometry(WIDTH, DEPTH), new THREE.MeshStandardMaterial({ map: quarryTile(WIDTH, DEPTH), roughness: 0.7 }));
  floor.rotation.x = -Math.PI / 2;
  floor.position.set(WIDTH / 2, 0, DEPTH / 2);
  floor.receiveShadow = true;
  g.add(floor);

  const wallMat = (w: number) => new THREE.MeshStandardMaterial({ map: subwayTile(w, HEIGHT), roughness: 0.35 });
  const back = new THREE.Mesh(new THREE.PlaneGeometry(WIDTH, HEIGHT), wallMat(WIDTH));
  back.position.set(WIDTH / 2, HEIGHT / 2, 0);
  back.receiveShadow = true;
  g.add(back);
  const left = new THREE.Mesh(new THREE.PlaneGeometry(DEPTH, HEIGHT), wallMat(DEPTH));
  left.rotation.y = Math.PI / 2;
  left.position.set(0, HEIGHT / 2, DEPTH / 2);
  left.receiveShadow = true;
  g.add(left);
  const right = left.clone();
  right.rotation.y = -Math.PI / 2;
  right.position.x = WIDTH;
  g.add(right);

  // The front wall has the pass window in it, x 4.2–6.2, y 1.0–1.85.
  const frontMat = wallMat(WIDTH);
  const front = new THREE.Group();
  const [px0, px1, py0, py1] = [4.2, 6.2, 1.0, 1.85];
  for (const [x0, x1, y0, y1] of [
    [0, px0, 0, HEIGHT],
    [px1, WIDTH, 0, HEIGHT],
    [px0, px1, 0, py0],
    [px0, px1, py1, HEIGHT],
  ] as const) {
    const m = new THREE.Mesh(new THREE.PlaneGeometry(x1 - x0, y1 - y0), frontMat);
    m.rotation.y = Math.PI;
    m.position.set((x0 + x1) / 2, (y0 + y1) / 2, DEPTH);
    front.add(m);
  }
  g.add(front);
  // The dining room beyond the pass: warm light, a counter, a hint of booths.
  const glow = new THREE.MeshBasicMaterial({ color: look.glow });
  const dining = new THREE.Mesh(new THREE.PlaneGeometry(10, 4), glow);
  dining.rotation.y = Math.PI;
  dining.position.set((px0 + px1) / 2, 1.5, DEPTH + 3);
  g.add(dining);
  const diningFloor = new THREE.Mesh(new THREE.PlaneGeometry(10, 3), paint(0x8a5a3c, 0.6));
  diningFloor.rotation.x = -Math.PI / 2;
  diningFloor.position.set((px0 + px1) / 2, 0, DEPTH + 1.5);
  g.add(diningFloor);
  span(px0 - 1, px1 + 1, 0, 0.95, DEPTH + 1.6, DEPTH + 2.2, paint(look.booths, 0.6), g); // booths
  span(px0 - 1, px1 + 1, 0.95, 1.4, DEPTH + 2.1, DEPTH + 2.2, paint(look.booths, 0.6), g); // booth backs
  if (look.lanterns) {
    // Red paper lanterns over the tables.
    const lantern = new THREE.MeshStandardMaterial({ color: 0xd8321e, emissive: 0xc0281a, emissiveIntensity: 0.9, roughness: 0.8 });
    for (let i = 0; i < 4; i++) {
      const l = new THREE.Mesh(new THREE.SphereGeometry(0.13, 16, 12), lantern);
      l.scale.y = 1.2;
      // Hung low enough to be seen through the pass.
      l.position.set(px0 - 0.1 + i * 0.75, 1.62 + (i % 2) * 0.08, DEPTH + 1.1 + (i % 2) * 0.35);
      g.add(l);
      const cord = new THREE.Mesh(new THREE.CylinderGeometry(0.004, 0.004, 1.2, 4), paint(0x222222));
      cord.position.set(l.position.x, l.position.y + 0.75, l.position.z);
      g.add(cord);
    }
  }
  span(px0 - 1, px1 + 1, 0.9, 0.95, DEPTH + 0.4, DEPTH + 0.9, paint(0xf2efe4, 0.4), g); // the counter
  // The opening's reveal, so the wall has a thickness.
  for (const x of [px0, px1]) span(x - 0.01, x + 0.01, py0, py1, DEPTH, DEPTH + 0.15, paint(0xe8e6de), g);
  span(px0, px1, py1 - 0.01, py1 + 0.01, DEPTH, DEPTH + 0.15, paint(0xe8e6de), g);
  span(px0, px1, py0 - 0.05, py0, DEPTH - 0.25, DEPTH + 0.25, steel(), g); // pass shelf
  span(px0, px1, py1, py1 + 0.06, DEPTH - 0.15, DEPTH, paint(0xd9d3c4), g); // heat lamp
  const ticketRail = new THREE.Vector3((px0 + px1) / 2, py1 - 0.04, DEPTH - 0.03);
  span(px0 + 0.1, px1 - 0.1, py1 - 0.03, py1 - 0.01, DEPTH - 0.04, DEPTH - 0.02, steel(), g);

  const ceiling = new THREE.Mesh(new THREE.PlaneGeometry(WIDTH, DEPTH), paint(0xf0eee6));
  ceiling.rotation.x = Math.PI / 2;
  ceiling.position.set(WIDTH / 2, HEIGHT, DEPTH / 2);
  g.add(ceiling);
  for (const [x, z] of [
    [2.4, 1.4],
    [5.2, 3.3],
  ] as const) {
    span(x - 0.7, x + 0.7, HEIGHT - 0.06, HEIGHT, z - 0.12, z + 0.12, new THREE.MeshStandardMaterial({ color: 0xffffff, emissive: 0xfff6e0, emissiveIntensity: 1.2 }), g);
  }
  // Baseboard cove.
  span(0, WIDTH, 0, 0.1, 0, 0.02, paint(0x3b2a24), g);

  // --- the dish line ----------------------------------------------------------
  const counterTop = 0.92;
  const tub = STATIONS.tub;
  // Dirty landing under the tub.
  const tubCounter = steel();
  highlight.tub.push(tubCounter);
  span(0.6, 1.85, counterTop - 0.04, counterTop, 0, 0.76, tubCounter, g);
  span(0.62, 1.83, 0, counterTop - 0.04, 0.05, 0.72, paint(0x8a939b, 0.5), g);
  // The bus tub: grey plastic, open on top.
  const tubMat = paint(0x6f7a84, 0.55);
  highlight.tub.push(tubMat);
  const [tx0, tx1, tz0, tz1, ty0, ty1] = [tub.min[0], tub.max[0], tub.min[2], tub.max[2], counterTop, counterTop + 0.18];
  span(tx0, tx1, ty0, ty0 + 0.015, tz0, tz1, tubMat, g);
  span(tx0, tx1, ty0, ty1, tz0, tz0 + 0.015, tubMat, g);
  span(tx0, tx1, ty0, ty1, tz1 - 0.015, tz1, tubMat, g);
  span(tx0, tx0 + 0.015, ty0, ty1, tz0, tz1, tubMat, g);
  span(tx1 - 0.015, tx1, ty0, ty1, tz0, tz1, tubMat, g);

  // Sink: a counter with a deep basin, faucet and the pre-rinse sprayer.
  const sinkMat = steel();
  highlight.sink.push(sinkMat);
  const [sx0, sx1, sz0, sz1, sb] = [1.97, 2.73, 0.12, 0.66, 0.62];
  span(1.85, sx0, counterTop - 0.04, counterTop, 0, 0.76, sinkMat, g);
  span(sx1, 2.9, counterTop - 0.04, counterTop, 0, 0.76, sinkMat, g);
  span(sx0, sx1, counterTop - 0.04, counterTop, 0, sz0, sinkMat, g);
  span(sx0, sx1, counterTop - 0.04, counterTop, sz1, 0.76, sinkMat, g);
  span(sx0, sx1, sb, sb + 0.01, sz0, sz1, sinkMat, g); // basin floor
  span(sx0, sx0 + 0.01, sb, counterTop, sz0, sz1, sinkMat, g);
  span(sx1 - 0.01, sx1, sb, counterTop, sz0, sz1, sinkMat, g);
  span(sx0, sx1, sb, counterTop, sz0, sz0 + 0.01, sinkMat, g);
  span(sx0, sx1, sb, counterTop, sz1 - 0.01, sz1, sinkMat, g);
  const water = new THREE.Mesh(new THREE.PlaneGeometry(sx1 - sx0 - 0.02, sz1 - sz0 - 0.02), new THREE.MeshStandardMaterial({ color: 0x9fd3e6, transparent: true, opacity: 0.55, roughness: 0.1 }));
  water.rotation.x = -Math.PI / 2;
  water.position.set((sx0 + sx1) / 2, sb + 0.12, (sz0 + sz1) / 2);
  g.add(water);
  // Suds.
  const sudsMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.9 });
  const sud = new THREE.SphereGeometry(1, 10, 8);
  for (let i = 0; i < 14; i++) {
    const s = new THREE.Mesh(sud, sudsMat);
    const a = i * 2.4;
    s.position.set((sx0 + sx1) / 2 + Math.cos(a) * (0.12 + (i % 3) * 0.08), sb + 0.125, (sz0 + sz1) / 2 + Math.sin(a) * (0.1 + (i % 2) * 0.08));
    s.scale.set(0.03 + (i % 4) * 0.008, 0.012, 0.03);
    g.add(s);
  }
  span(0.6, 4.8, counterTop, 1.2, 0, 0.02, sinkMat, g); // backsplash
  // Faucet at the back, and the pre-rinse sprayer on its riser at the left corner.
  const faucetX = (sx0 + sx1) / 2;
  const pipe = steel();
  box(0.025, 0.2, 0.025, pipe, faucetX, counterTop + 0.1, 0.05, g);
  box(0.025, 0.025, 0.18, pipe, faucetX, counterTop + 0.2, 0.13, g);
  const riserX = sx0 + 0.06;
  box(0.03, 0.85, 0.03, pipe, riserX, counterTop + 0.42, 0.05, g);
  box(0.03, 0.03, 0.2, pipe, riserX, counterTop + 0.83, 0.14, g);
  const hose = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, 0.42, 8), paint(0x30353a, 0.5));
  hose.position.set(riserX, counterTop + 0.62, 0.24);
  g.add(hose);
  box(0.05, 0.11, 0.05, paint(0x30353a, 0.5), riserX, counterTop + 0.36, 0.24, g); // spray head

  // Drainboard, sloping to the sink, with the rack on it.
  const rackMat = steel();
  highlight.rack.push(rackMat);
  span(2.9, 3.92, counterTop - 0.04, counterTop, 0, 0.76, rackMat, g);
  span(2.92, 3.9, 0, counterTop - 0.04, 0.05, 0.72, paint(0x8a939b, 0.5), g);

  // The dish machine: a cabinet, a hood that lifts, a status lamp.
  const m = STATIONS.machine;
  const machineMat = steel();
  highlight.machine.push(machineMat);
  span(m.min[0], m.max[0], 0, 0.85, m.min[2], m.max[2], machineMat, g);
  span(m.min[0], m.max[0], 1.85, 2.0, m.min[2] + 0.1, m.max[2] - 0.1, machineMat, g); // motor housing, above the lifted hood
  for (const x of [m.min[0] + 0.02, m.max[0] - 0.04]) span(x, x + 0.02, 0.85, 1.85, m.min[2] + 0.02, m.min[2] + 0.06, machineMat, g); // hood guides
  const hood = new THREE.Group();
  const hoodMat = steel();
  highlight.machine.push(hoodMat);
  span(m.min[0], m.max[0], 0.86, 1.37, m.min[2], m.min[2] + 0.02, hoodMat, hood);
  span(m.min[0], m.max[0], 0.86, 1.37, m.max[2] - 0.02, m.max[2], hoodMat, hood);
  span(m.min[0], m.min[0] + 0.02, 0.86, 1.37, m.min[2], m.max[2], hoodMat, hood);
  span(m.max[0] - 0.02, m.max[0], 0.86, 1.37, m.min[2], m.max[2], hoodMat, hood);
  span(m.min[0], m.max[0], 1.35, 1.37, m.min[2], m.max[2], hoodMat, hood);
  span(m.min[0] + 0.2, m.max[0] - 0.2, 1.2, 1.23, m.max[2], m.max[2] + 0.06, paint(0x2b2d42, 0.4), hood); // handle
  g.add(hood);
  const lamp = new THREE.MeshStandardMaterial({ color: 0x333333, emissive: 0x22c55e, emissiveIntensity: 1 });
  box(0.05, 0.05, 0.02, lamp, m.max[0] - 0.1, 0.75, m.max[2] + 0.01, g);
  // The chamber, seen when the hood is up: a dark back, the rack rails and the wash arms.
  const chamber = paint(0x59636c, 0.4);
  span(m.min[0] + 0.03, m.max[0] - 0.03, 0.85, 0.86, m.min[2] + 0.03, m.max[2] - 0.03, chamber, g);
  span(m.min[0] + 0.03, m.max[0] - 0.03, 0.86, 1.35, m.min[2], m.min[2] + 0.03, chamber, g);
  for (const z of [m.min[2] + 0.12, m.max[2] - 0.12]) span(m.min[0] + 0.03, m.max[0] - 0.03, 0.86, 0.875, z - 0.01, z + 0.01, steel(), g);
  for (const y of [0.85, 1.32]) {
    const arm = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, 0.6, 8), steel());
    arm.rotation.z = Math.PI / 2;
    arm.position.set((m.min[0] + m.max[0]) / 2, y, (m.min[2] + m.max[2]) / 2);
    g.add(arm);
  }
  // A side table where the clean rack comes out.
  span(m.max[0], 5.3, counterTop - 0.04, counterTop, 0, 0.76, steel(), g);
  span(m.max[0] + 0.02, 5.28, 0, counterTop - 0.04, 0.05, 0.72, paint(0x8a939b, 0.5), g);

  // --- the clean shelf ---------------------------------------------------------
  const sh = STATIONS.shelf;
  const wire = new THREE.MeshStandardMaterial({ color: 0xd8dde2, metalness: 0.6, roughness: 0.4 });
  highlight.shelf.push(wire);
  const shelfLevels = [0.15, 0.6, 1.05, 1.5];
  for (const [x, z] of [
    [sh.min[0], sh.min[2]],
    [sh.min[0], sh.max[2]],
    [sh.max[0] - 0.02, sh.min[2]],
    [sh.max[0] - 0.02, sh.max[2]],
  ] as const) {
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.014, 0.014, sh.max[1], 8), wire);
    post.position.set(x, sh.max[1] / 2, z);
    g.add(post);
  }
  // Each shelf a wire grid: a frame, and bars front to back every 5 cm.
  const bar = new THREE.BoxGeometry(sh.max[0] - sh.min[0] - 0.02, 0.008, 0.006);
  for (const y of shelfLevels) {
    span(sh.min[0] - 0.005, sh.min[0] + 0.005, y, y + 0.03, sh.min[2], sh.max[2], wire, g);
    span(sh.max[0] - 0.03, sh.max[0] - 0.02, y, y + 0.03, sh.min[2], sh.max[2], wire, g);
    for (let z = sh.min[2]; z <= sh.max[2] + 1e-6; z += 0.05) {
      const m = new THREE.Mesh(bar, wire);
      m.position.set((sh.min[0] + sh.max[0] - 0.02) / 2, y - 0.004, z);
      g.add(m);
    }
  }

  // --- details -----------------------------------------------------------------
  // The swing door to the dining room, porthole and kick plate.
  const doorMat = paint(look.door, 0.5);
  span(0, 0.05, 0, 2.1, 2.4, 3.3, doorMat, g);
  span(0.05, 0.06, 0, 0.3, 2.42, 3.28, steel(), g);
  const port = new THREE.Mesh(new THREE.CircleGeometry(0.14, 24), new THREE.MeshBasicMaterial({ color: 0xffe0a8 }));
  port.rotation.y = Math.PI / 2;
  port.position.set(0.055, 1.55, 2.85);
  g.add(port);
  // Trash can by the tub.
  const can = new THREE.Mesh(new THREE.CylinderGeometry(0.24, 0.21, 0.8, 20), paint(0x4a5560, 0.6));
  can.position.set(0.3, 0.4, 0.4);
  can.castShadow = true;
  g.add(can);
  const bag = new THREE.Mesh(new THREE.TorusGeometry(0.235, 0.02, 8, 24), paint(0x111111, 0.4));
  bag.rotation.x = Math.PI / 2;
  bag.position.set(0.3, 0.8, 0.4);
  g.add(bag);
  // Mop bucket in the corner.
  const bucket = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.17, 0.35, 16), paint(0xf2c230, 0.5));
  bucket.position.set(0.35, 0.18, 4.6);
  g.add(bucket);
  const mop = new THREE.Mesh(new THREE.CylinderGeometry(0.015, 0.015, 1.4, 8), paint(0x9a7b4f));
  mop.position.set(0.3, 0.8, 4.65);
  mop.rotation.z = 0.25;
  g.add(mop);
  // Anti-fatigue mat in front of the sink.
  span(1.0, 3.6, 0, 0.012, 0.8, 1.6, paint(0x1d1f22, 0.95), g);
  // Signs: wash your hands over the tub, specials by the pass.
  const sign = new THREE.Mesh(new THREE.PlaneGeometry(0.4, 0.25), new THREE.MeshStandardMaterial({ map: handWashSign(), roughness: 0.6 }));
  sign.position.set(1.2, 1.65, 0.01);
  g.add(sign);
  const board = new THREE.Mesh(new THREE.PlaneGeometry(0.8, 0.6), new THREE.MeshStandardMaterial({ map: specialsBoard(restaurantName, look.specials), roughness: 0.9 }));
  board.rotation.y = Math.PI;
  board.position.set(3.3, 1.6, DEPTH - 0.01);
  g.add(board);
  // A radio on a wall shelf above the machine.
  span(4.95, 5.6, 1.6, 1.62, 0, 0.25, paint(0x9a7b4f), g);
  span(5.05, 5.4, 1.62, 1.8, 0.04, 0.16, paint(0xb5442e, 0.5), g);
  // The wall clock above the sink.
  const face = new THREE.Mesh(new THREE.CylinderGeometry(0.17, 0.17, 0.03, 32), paint(0xfdfdf8, 0.5));
  face.rotation.x = Math.PI / 2;
  face.position.set(2.35, 2.3, 0.02);
  g.add(face);
  const rim = new THREE.Mesh(new THREE.TorusGeometry(0.17, 0.012, 8, 32), paint(0x2b2d42, 0.4));
  rim.position.set(2.35, 2.3, 0.035);
  g.add(rim);
  const hand = (len: number, w: number) => {
    const pivot = new THREE.Group();
    pivot.position.set(2.35, 2.3, 0.04);
    const h = new THREE.Mesh(new THREE.BoxGeometry(w, len, 0.004), paint(0x2b2d42, 0.4));
    h.position.y = len / 2;
    pivot.add(h);
    g.add(pivot);
    return pivot;
  };
  const hourHand = hand(0.09, 0.014);
  const minuteHand = hand(0.14, 0.009);

  return {
    group: g,
    hood,
    lamp,
    hourHand,
    minuteHand,
    highlight,
    anchors: {
      sink: new THREE.Vector3((sx0 + sx1) / 2, sb + 0.13, (sz0 + sz1) / 2 + 0.05),
      rack: new THREE.Vector3(3.44, counterTop, 0.4),
      machineRack: new THREE.Vector3((m.min[0] + m.max[0]) / 2, 0.86, (m.min[2] + m.max[2]) / 2),
      ticketRail,
    },
    machineBox: m,
    shelfSpot(kind, i) {
      const spot = SHELF_SPOTS[kind];
      const [dx, dy, dz, flip] = spot.at(i);
      return { pos: new THREE.Vector3(sh.min[0] + 0.25 + dx, shelfLevels[spot.level]! + dy, sh.min[2] + spot.z + dz), flip, turn: kind === "wok" ? Math.PI / 2 : 0 };
    },
    keyLight: { from: new THREE.Vector3(3, 2.9, 3.2), at: new THREE.Vector3(2.6, 0.5, 0.6) },
    passLight: new THREE.Vector3(5.2, 1.6, 4.6),
  };
}
